import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PlayIcon, PauseIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import {
    FaClockRotateLeft, FaMagnifyingGlass, FaFileExcel, FaPhone, FaPhoneSlash, FaHeadset, FaRobot,
    FaPersonWalking, FaStore, FaChevronLeft, FaChevronRight, FaClipboardCheck,
} from 'react-icons/fa6';
import * as XLSX from 'xlsx';
import { SaveGestion } from '../SaveGestion/SaveGestion';
import { useSip } from '../../SipContext';
import { ModalConversacion, colorSentimiento, formatDuracion } from './ModalConversacion';
import { ModalRevisionGestion, diferenciasRevision } from './ModalRevisionGestion';

const POR_PAGINA = 7;
const ES_AUTOMATICA = (item) => item.Origen === 'AUTO_CDR';

/**
 * Fecha de pago sin desfase de zona horaria. Las fechas "vacías" del sistema
 * (2000-01-01 / 1900-01-01, con o sin corrimiento de hora) se muestran como '-'.
 */
const formatFechaPago = (fechaStr) => {
    if (!fechaStr) return '-';
    const datePart = String(fechaStr).split('T')[0];
    const [year, month, day] = datePart.split('-');
    if (!year || Number(year) <= 2000) return '-';
    if (month && day) return `${parseInt(day, 10)}/${parseInt(month, 10)}/${year}`;
    return new Date(fechaStr).toLocaleDateString('es-EC');
};

const tieneTelefono = (tel) => tel && tel !== '0' && tel !== 0;

// ─── Estilos por tipo / estado / resultado (paleta clara del mockup) ───────────
const TIPO = {
    OPERADOR: { clase: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/70 dark:text-indigo-300 dark:border-indigo-700/50', Icono: FaHeadset },
    'OPERADOR CXC': { clase: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/70 dark:text-indigo-300 dark:border-indigo-700/50', Icono: FaHeadset },
    COBRADOR: { clase: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800/50', Icono: FaPersonWalking },
    VENDEDOR: { clase: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/60 dark:text-sky-300 dark:border-sky-800/50', Icono: FaStore },
};

const claseEstado = (estado) => ((estado || '').toUpperCase().includes('TELEF')
    ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800/50'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800/50');

const claseResultado = (item) => {
    const r = (item.Resultado || '').toUpperCase();
    const ct = (item['Tipo Contrato'] || '').toUpperCase();
    if (r.includes('COMPROMISO') || r.includes('PAGO REALIZADO') || r.includes('INDICA PAGO')) return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800/50';
    if (r.includes('NEGATIVA') || r.includes('NO HAY NADIE') || r.includes('EQUIVOCADO') || r.includes('FALLEC')) return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-400 dark:border-rose-800/60';
    if (ct.startsWith('NO CONTACTO')) return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/50';
    if (ct.includes('INDIRECTO')) return 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-purple-950/50 dark:text-purple-300 dark:border-purple-800/50';
    return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800/50';
};

const capitalizar = (t) => (t ? t.charAt(0) + t.slice(1).toLowerCase() : '');

// ─── Filtro ────────────────────────────────────────────────────────────────────
const FILTROS_TIPO = [
    { valor: '', etiqueta: 'Todos los tipos' },
    { valor: 'OPERADOR', etiqueta: 'Operador' },
    { valor: 'AUTOMATICA', etiqueta: 'Automática' },
    { valor: 'COBRADOR', etiqueta: 'Cobrador' },
    { valor: 'VENDEDOR', etiqueta: 'Vendedor' },
];

export const esGestionContestada = (item) => {
    if (!item) return false;
    const r = (item.Resultado || '').toUpperCase().trim();
    const ct = (item['Tipo Contrato'] || item.Tipo_Contrato || item.TipoContacto || item.Contacto || '').toUpperCase().trim();

    // Si explícitamente se registró como NO CONTACTO / NO CONTACTO DIRECTO
    if (ct.startsWith('NO CONTACTO') || ct === 'NO CONTACTO' || ct === 'NO CONTACTADO') {
        if (r.includes('COMPROMISO') || r.includes('PAGO REALIZADO') || r.includes('INDICA PAGO')) {
            return true;
        }
        return false;
    }

    // Tipo de contacto DIRECTO o INDIRECTO (contacto efectivo con titular o tercero)
    if (ct.includes('DIRECTO') || ct.includes('INDIRECTO')) {
        return true;
    }

    // Resultados que indican que no hubo contestación
    if (
        r.includes('NO CONTESTA') ||
        r.includes('NO HAY NADIE') ||
        r.includes('GRABADORA') ||
        r.includes('BUZON') ||
        r.includes('APAGADO') ||
        r.includes('FUERA DE SERVICIO') ||
        r.includes('EQUIVOCADO') ||
        r.includes('OCUPADO')
    ) {
        return false;
    }

    // Para llamadas automáticas del CDR
    if (item.Origen === 'AUTO_CDR') {
        return Boolean((item.DuracionLlamada && Number(item.DuracionLlamada) > 0) || item.UrlGrabacion || item.Resumen);
    }

    // Resultados que evidencian conversación / contacto exitoso
    if (
        r.includes('COMPROMISO') ||
        r.includes('NEGATIVA') ||
        r.includes('PAGO') ||
        r.includes('MENSAJE') ||
        r.includes('RECLAMO') ||
        r.includes('ACUERDO') ||
        r.includes('TITULAR') ||
        r.includes('TERCERO') ||
        r.includes('NOTIF')
    ) {
        return true;
    }

    if (item.DuracionLlamada && Number(item.DuracionLlamada) > 0) {
        return true;
    }

    return false;
};

const cumpleContacto = (item, filtro) => {
    if (!filtro || filtro === 'TODAS') return true;
    const contestada = esGestionContestada(item);
    if (filtro === 'CONTESTADAS') return contestada;
    if (filtro === 'NO_CONTESTADAS') return !contestada;
    return true;
};

const cumpleTipo = (item, tipo) => {
    if (!tipo) return true;
    if (tipo === 'AUTOMATICA') return ES_AUTOMATICA(item);
    if (tipo === 'OPERADOR') return !ES_AUTOMATICA(item) && (item.Tipo || '').startsWith('OPERADOR');
    return item.Tipo === tipo;
};

const cumpleTexto = (item, texto) => {
    if (!texto) return true;
    const t = texto.toLowerCase();
    return [item.Operador_Cobrador, item.Usuario, item.Notas, item.Resultado, item.Telefono, item.Nombre]
        .some((v) => String(v || '').toLowerCase().includes(t));
};

// ─── Excel ─────────────────────────────────────────────────────────────────────
const exportarExcel = (filas, idCompra) => {
    const datos = filas.map((g) => ({
        Tipo: g.Tipo || '',
        Automatica: ES_AUTOMATICA(g) ? 'SI' : 'NO',
        Contestada: esGestionContestada(g) ? 'SI' : 'NO',
        Fecha: g.Fecha ? new Date(g.Fecha).toLocaleString('es-EC') : '',
        Telefono: g.Telefono || '',
        Operador: g.Operador_Cobrador || '',
        Usuario: g.Usuario || '',
        Estado: g.Estado || '',
        'Tipo contacto': g['Tipo Contrato'] || '',
        Resultado: g.Resultado || '',
        Valor: Number(g.Valor || 0),
        'Fecha pago': formatFechaPago(g.FechaPago) === '-' ? '' : formatFechaPago(g.FechaPago),
        'Estado pago': g.ESTADO_PAGO || '',
        Parentesco: g.Nombre || '',
        Direccion: g.Direccion || '',
        Notas: g.Notas || '',
        'Duracion llamada (s)': g.DuracionLlamada ?? '',
        'Animo cliente': g.SentimientoPersona || '',
        Resumen: g.Resumen || '',
        Grabacion: g.UrlGrabacion || '',
        Revisada: ES_AUTOMATICA(g) ? (g.Rev_Fecha ? 'SI' : 'NO') : '',
        'Revisado por': g.Rev_Usuario || '',
        'Diferencias IA': diferenciasRevision(g) ?? '',
        'Calidad transcripcion': g.Rev_CalidadTranscripcion ?? '',
        'Errores transcripcion': g.Rev_TiposError || '',
        'Fragmento corregido': g.Rev_FragmentoCorregido || '',
        'Observacion revision': g.Rev_Observacion || '',
    }));
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Gestiones');
    XLSX.writeFile(wb, `Gestiones_${idCompra || 'credito'}.xlsx`);
};

/** Botón de la columna Observación: "Revisar" o el resultado de la revisión ya hecha. */
function BotonRevision({ item, onClick }) {
    const dif = diferenciasRevision(item);
    const [clase, texto, titulo] = dif === null
        ? ['bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50 dark:bg-[#161F30] dark:text-indigo-300 dark:border-indigo-700/60 dark:hover:bg-slate-800', 'Revisar', 'Comparar la gestión automática con lo que realmente se dijo']
        : dif === 0
            ? ['bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800/50', '✓ Coincide', `Revisada por ${item.Rev_Usuario || '-'}`]
            : ['bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800/60', `✗ ${dif} diferencia${dif === 1 ? '' : 's'}`, `Revisada por ${item.Rev_Usuario || '-'}`];
    return (
        <button type="button" onClick={onClick} title={titulo}
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-semibold transition-colors ${clase}`}>
            <FaClipboardCheck className="text-[10px]" /> {texto}
        </button>
    );
}

export function HistorialGestiones({ historialAPI, estadoGestion, data, onRecargar }) {
    const { makeCall, phoneAvailable } = useSip();
    const [showSaveGestion, setShowSaveGestion] = useState(false);
    const [telefonoSeleccionado, setTelefonoSeleccionado] = useState('');
    const [gestionDetalle, setGestionDetalle] = useState(null);
    const [gestionRevision, setGestionRevision] = useState(null);
    const [texto, setTexto] = useState('');
    const [tipo, setTipo] = useState('');
    const [filtroContacto, setFiltroContacto] = useState('TODAS'); // 'TODAS' | 'CONTESTADAS' | 'NO_CONTESTADAS'
    const [pagina, setPagina] = useState(1);
    // Un solo reproductor para toda la tabla: al tocar otra grabación se detiene la anterior
    const audioRef = useRef(null);
    const [sonando, setSonando] = useState(null);

    useEffect(() => () => audioRef.current?.pause(), []);

    const conteos = useMemo(() => {
        let contestadas = 0;
        (historialAPI || []).forEach((g) => {
            if (esGestionContestada(g)) contestadas += 1;
        });
        const todas = (historialAPI || []).length;
        return {
            todas,
            contestadas,
            noContestadas: Math.max(0, todas - contestadas),
        };
    }, [historialAPI]);

    const filtradas = useMemo(
        () => (historialAPI || []).filter(
            (g) => cumpleTipo(g, tipo) && cumpleContacto(g, filtroContacto) && cumpleTexto(g, texto.trim()),
        ),
        [historialAPI, tipo, filtroContacto, texto],
    );
    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
    const paginaActual = Math.min(pagina, totalPaginas);
    const visibles = filtradas.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA);

    useEffect(() => { setPagina(1); }, [texto, tipo, filtroContacto, historialAPI]);

    const alternarAudio = (url) => {
        if (sonando === url) {
            audioRef.current?.pause();
            setSonando(null);
            return;
        }
        audioRef.current?.pause();
        const audio = new Audio(url);
        audio.onended = () => setSonando(null);
        audio.onerror = () => setSonando(null);
        audioRef.current = audio;
        audio.play().then(() => setSonando(url)).catch(() => setSonando(null));
    };

    const llamar = (telefono) => {
        setTelefonoSeleccionado(telefono);
        if (phoneAvailable) {
            makeCall(telefono, data?.idCompra ?? null, { clientName: data?.Cliente ?? '' });
        } else {
            setShowSaveGestion(true);
        }
    };

    if (!historialAPI || historialAPI.length === 0) {
        return <div className="text-slate-400 text-center py-8">Sin gestiones registradas.</div>;
    }

    const th = 'py-3 px-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap';
    const desde = filtradas.length ? (paginaActual - 1) * POR_PAGINA + 1 : 0;
    const hasta = Math.min(paginaActual * POR_PAGINA, filtradas.length);

    return (
        <section className="bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
            {/* Encabezado y controles */}
            <div className="p-3 sm:px-4 bg-slate-50 dark:bg-[#0D131F] border-b border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <FaClockRotateLeft className="text-indigo-600 dark:text-indigo-400" />
                        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-white">Historial de Gestiones</h2>
                    </div>

                    {/* Botones de filtro de contacto (Todas / Contestadas / No contestadas) */}
                    <div className="inline-flex items-center p-0.5 bg-slate-200/80 dark:bg-[#161F30] rounded-lg border border-slate-300/80 dark:border-slate-700 text-xs shadow-2xs">
                        <button
                            type="button"
                            onClick={() => setFiltroContacto('TODAS')}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer ${
                                filtroContacto === 'TODAS'
                                    ? 'bg-white dark:bg-indigo-600 text-slate-900 dark:text-white shadow-xs font-bold'
                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <span>Todas</span>
                            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                                filtroContacto === 'TODAS'
                                    ? 'bg-slate-100 dark:bg-indigo-700 text-slate-700 dark:text-white'
                                    : 'bg-slate-300/70 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                            }`}>
                                {conteos.todas}
                            </span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setFiltroContacto(filtroContacto === 'CONTESTADAS' ? 'TODAS' : 'CONTESTADAS')}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer ${
                                filtroContacto === 'CONTESTADAS'
                                    ? 'bg-emerald-600 text-white shadow-xs font-bold'
                                    : 'text-slate-700 dark:text-slate-300 hover:text-emerald-700 dark:hover:text-emerald-400 hover:bg-white/50 dark:hover:bg-slate-800/50'
                            }`}
                            title="Filtrar por gestiones contestadas / contacto efectivo"
                        >
                            <FaPhone className="text-[10px]" />
                            <span>Contestadas</span>
                            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                                filtroContacto === 'CONTESTADAS'
                                    ? 'bg-emerald-700 text-white'
                                    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300'
                            }`}>
                                {conteos.contestadas}
                            </span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setFiltroContacto(filtroContacto === 'NO_CONTESTADAS' ? 'TODAS' : 'NO_CONTESTADAS')}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer ${
                                filtroContacto === 'NO_CONTESTADAS'
                                    ? 'bg-amber-600 text-white shadow-xs font-bold'
                                    : 'text-slate-700 dark:text-slate-300 hover:text-amber-700 dark:hover:text-amber-400 hover:bg-white/50 dark:hover:bg-slate-800/50'
                            }`}
                            title="Filtrar por gestiones no contestadas o sin respuesta"
                        >
                            <FaPhoneSlash className="text-[10px]" />
                            <span>No contestadas</span>
                            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                                filtroContacto === 'NO_CONTESTADAS'
                                    ? 'bg-amber-700 text-white'
                                    : 'bg-slate-300/70 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                            }`}>
                                {conteos.noContestadas}
                            </span>
                        </button>
                    </div>
                </div>
                <div className="flex items-center flex-wrap gap-2 text-xs">
                    <div className="relative">
                        <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                            <FaMagnifyingGlass className="text-[11px]" />
                        </span>
                        <input
                            type="text"
                            value={texto}
                            onChange={(e) => setTexto(e.target.value)}
                            placeholder="Filtrar por operador, notas..."
                            className="pl-8 pr-3 py-1 text-xs rounded-lg bg-white border border-slate-300 text-slate-800 placeholder-slate-400 dark:bg-[#161F30] dark:border-slate-700 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 w-48 sm:w-60"
                        />
                    </div>
                    <select
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value)}
                        className="py-1 px-2.5 text-xs rounded-lg bg-white border border-slate-300 text-slate-700 dark:bg-[#161F30] dark:border-slate-700 dark:text-slate-200 focus:ring-1 focus:ring-indigo-500"
                    >
                        {FILTROS_TIPO.map((f) => <option key={f.valor} value={f.valor}>{f.etiqueta}</option>)}
                    </select>
                    <button
                        type="button"
                        onClick={() => exportarExcel(filtradas, data?.idCompra)}
                        disabled={!filtradas.length}
                        className="px-2.5 py-1 text-xs font-medium rounded-lg bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 dark:bg-[#161F30] dark:hover:bg-slate-700 dark:text-slate-200 dark:border-slate-700 flex items-center gap-1.5 transition disabled:opacity-50"
                    >
                        <FaFileExcel className="text-emerald-600 dark:text-emerald-400" />
                        <span>Excel</span>
                    </button>
                </div>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-100 dark:bg-[#161F30] border-b border-slate-200 dark:border-slate-800">
                            <th className={th}>Tipo / Canal</th>
                            <th className={th}>Llamada</th>
                            <th className={th}>Teléfono</th>
                            <th className={th}>Fecha y Hora</th>
                            <th className={th}>Operador</th>
                            <th className={th}>Estado</th>
                            <th className={th}>Resultado</th>
                            <th className={th}>Valores</th>
                            <th className={`${th} min-w-[260px]`}>Notas y Observaciones</th>
                            <th className={th}>Observación</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800/80 text-xs">
                        {visibles.length === 0 && (
                            <tr>
                                <td colSpan={10} className="py-6 text-center text-slate-400 dark:text-slate-500">
                                    {filtroContacto === 'CONTESTADAS'
                                        ? 'No se encontraron gestiones contestadas con los filtros actuales.'
                                        : 'Ninguna gestión coincide con el filtro.'}
                                </td>
                            </tr>
                        )}
                        {visibles.map((item) => {
                            const auto = ES_AUTOMATICA(item);
                            const estiloTipo = TIPO[item.Tipo] || TIPO.OPERADOR;
                            const IconoTipo = estiloTipo.Icono;
                            const fechaPago = formatFechaPago(item.FechaPago);
                            const conValor = Number(item.Valor || 0) > 0 || fechaPago !== '-';
                            return (
                                <tr key={item.idCbo_GestionesDeCobranzas}
                                    className={`${auto ? 'bg-violet-50/50 dark:bg-indigo-950/20' : ''} hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors`}>
                                    {/* Tipo / Canal */}
                                    <td className="py-3 px-3 whitespace-nowrap">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold border ${estiloTipo.clase}`}>
                                                <IconoTipo className="text-[10px]" /> {item.Tipo || '-'}
                                            </span>
                                            {auto && (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-600 text-white"
                                                    title="Registrada automáticamente a partir de la llamada">
                                                    <FaRobot className="text-[9px]" /> AUTOMÁTICA
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Llamada */}
                                    <td className="py-3 px-3 whitespace-nowrap">
                                        {auto ? (
                                            <div className="flex items-center gap-1.5">
                                                <span className="inline-flex items-center gap-1 font-mono text-[11px] text-slate-500">
                                                    <FaHeadset className="text-slate-400 dark:text-slate-500 text-[10px]" /> {formatDuracion(item.DuracionLlamada) || '0:00'}
                                                </span>
                                                {item.UrlGrabacion && (
                                                    <button
                                                        type="button"
                                                        onClick={() => alternarAudio(item.UrlGrabacion)}
                                                        className="p-1.5 bg-violet-100 hover:bg-violet-200 dark:bg-violet-900/60 dark:hover:bg-violet-800 rounded-full transition-colors"
                                                        title={sonando === item.UrlGrabacion ? 'Pausar grabación' : 'Escuchar grabación'}
                                                    >
                                                        {sonando === item.UrlGrabacion
                                                            ? <PauseIcon className="w-3.5 h-3.5 text-violet-700 dark:text-violet-200" />
                                                            : <PlayIcon className="w-3.5 h-3.5 text-violet-700 dark:text-violet-200" />}
                                                    </button>
                                                )}
                                                {item.SentimientoPersona && (
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${colorSentimiento(item.SentimientoPersona)}`}>
                                                        {item.SentimientoPersona.replace(/_/g, ' ')}
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => setGestionDetalle(item)}
                                                    className="p-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full transition-colors"
                                                    title="Ver resumen y conversación"
                                                >
                                                    <ChatBubbleLeftRightIcon className="w-3.5 h-3.5 text-slate-700 dark:text-slate-200" />
                                                </button>
                                            </div>
                                        ) : <span className="text-slate-400 font-mono text-[11px]">-</span>}
                                    </td>

                                    {/* Teléfono */}
                                    <td className="py-3 px-3 whitespace-nowrap">
                                        {tieneTelefono(item.Telefono) ? (
                                            <button
                                                type="button"
                                                onClick={() => llamar(item.Telefono)}
                                                className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-mono font-medium hover:underline"
                                                title={phoneAvailable ? `Llamar a ${item.Telefono}` : `Registrar gestión para ${item.Telefono}`}
                                            >
                                                <FaPhone className="text-emerald-600 dark:text-emerald-400 text-[10px]" />
                                                {item.Telefono}
                                            </button>
                                        ) : (
                                            <span className="text-slate-400 font-mono">
                                                {item.Tipo === 'COBRADOR' ? 'Visita domiciliaria' : '-'}
                                            </span>
                                        )}
                                    </td>

                                    {/* Fecha y hora */}
                                    <td className="py-3 px-3 whitespace-nowrap text-slate-700 dark:text-slate-300 font-medium">
                                        {item.Fecha ? new Date(item.Fecha).toLocaleString('es-EC') : '-'}
                                    </td>

                                    {/* Operador */}
                                    <td className="py-3 px-3 whitespace-nowrap">
                                        <span className="font-medium text-slate-800 dark:text-slate-200">{item.Operador_Cobrador || '-'}</span>
                                        {item.Usuario && <span className="text-[10px] text-slate-500 dark:text-slate-400 block font-mono">{item.Usuario}</span>}
                                    </td>

                                    {/* Estado */}
                                    <td className="py-3 px-3 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${claseEstado(item.Estado)}`}>
                                            {item.Estado || '-'}
                                        </span>
                                    </td>

                                    {/* Resultado + tipo de contacto */}
                                    <td className="py-3 px-3 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${claseResultado(item)}`}>
                                            {item.Resultado || '-'}
                                        </span>
                                        {item['Tipo Contrato'] && (
                                            <span className="text-[10px] text-slate-500 dark:text-slate-400 block mt-0.5">{capitalizar(item['Tipo Contrato'])}</span>
                                        )}
                                    </td>

                                    {/* Valores */}
                                    <td className="py-3 px-3 whitespace-nowrap">
                                        {conValor ? (
                                            <>
                                                <div className="text-[11px] font-semibold text-slate-800 dark:text-slate-300">${Number(item.Valor || 0).toFixed(2)}</div>
                                                {fechaPago !== '-' && <div className="text-[10px] text-slate-500 dark:text-slate-400">Pago: {fechaPago}</div>}
                                                {item.ESTADO_PAGO && <div className="text-[10px] font-semibold text-amber-700 dark:text-amber-300">{item.ESTADO_PAGO}</div>}
                                            </>
                                        ) : <span className="text-[11px] text-slate-400 font-mono">-</span>}
                                    </td>

                                    {/* Notas */}
                                    <td className="py-3 px-3 text-slate-700 dark:text-slate-300 leading-snug min-w-[260px] max-w-lg whitespace-normal break-words"
                                        title={item.Notas || item.Observacion || ''}>
                                        <p className={`uppercase ${auto ? 'font-mono text-slate-600 dark:text-slate-300' : 'text-slate-800 dark:text-slate-200'}`}>
                                            {item.Notas || item.Observacion || '-'}
                                        </p>
                                        {(item.Nombre || item.Direccion) && (
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                {[item.Nombre && `Contacto: ${item.Nombre}`, item.Direccion].filter(Boolean).join(' · ')}
                                            </p>
                                        )}
                                    </td>

                                    {/* Observación: revisión humana de la gestión automática */}
                                    <td className="py-3 px-3 whitespace-nowrap">
                                        {auto ? <BotonRevision item={item} onClick={() => setGestionRevision(item)} /> : <span className="text-slate-400 font-mono text-[11px]">-</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pie y paginación */}
            <div className="px-4 py-3 bg-slate-50 dark:bg-[#0D131F] border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
                <div>
                    Mostrando <span className="font-semibold text-slate-800 dark:text-slate-200">{desde}</span> a <span className="font-semibold text-slate-800 dark:text-slate-200">{hasta}</span> de{' '}
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{filtradas.length}</span> {filtroContacto === 'CONTESTADAS' ? 'gestiones contestadas' : filtroContacto === 'NO_CONTESTADAS' ? 'gestiones no contestadas' : 'gestiones registradas'}
                </div>
                {totalPaginas > 1 && (
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            disabled={paginaActual === 1}
                            onClick={() => setPagina(paginaActual - 1)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-[#161F30] dark:text-slate-300 dark:hover:bg-slate-700 disabled:text-slate-300 dark:disabled:text-slate-600 disabled:cursor-not-allowed"
                        >
                            <FaChevronLeft className="text-[10px]" /> Anterior
                        </button>
                        {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((n) => (
                            <button
                                key={n}
                                type="button"
                                onClick={() => setPagina(n)}
                                className={`px-2.5 py-1 rounded border font-medium ${n === paginaActual
                                    ? 'border-indigo-600 bg-indigo-600 text-white'
                                    : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-[#161F30] dark:text-slate-300 dark:hover:bg-slate-700'}`}
                            >
                                {n}
                            </button>
                        ))}
                        <button
                            type="button"
                            disabled={paginaActual === totalPaginas}
                            onClick={() => setPagina(paginaActual + 1)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-[#161F30] dark:text-slate-300 dark:hover:bg-slate-700 disabled:text-slate-300 dark:disabled:text-slate-600 disabled:cursor-not-allowed"
                        >
                            Siguiente <FaChevronRight className="text-[10px]" />
                        </button>
                    </div>
                )}
            </div>

            <ModalConversacion gestion={gestionDetalle} onClose={() => setGestionDetalle(null)} />
            <ModalRevisionGestion
                gestion={gestionRevision}
                idCompra={data?.idCompra}
                onClose={() => setGestionRevision(null)}
                onGuardado={onRecargar}
            />
            {/* Modal SaveGestion para registrar gestión con el teléfono seleccionado */}
            <SaveGestion
                isOpen={showSaveGestion}
                onClose={() => setShowSaveGestion(false)}
                estadoGestion={estadoGestion}
                datosCobranza={{ Celular: telefonoSeleccionado }}
                data={data}
            />
        </section>
    );
}
